import { useState } from "react";

import { Button, Stack, TextField } from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { useAPI, useShipmentPageContext } from "hooks";

interface Props {
  postQuestionURL: string;
  // Fires once the answer is persisted. The caller owns any analytics, so this
  // shared input stays free of them. Required rather than optional: a consumer
  // that silently forgets it is the exact gap this component was uninstrumented
  // by for so long.
  onAnswered: VoidFunction;
}

export function SubmitAnswerMenu(props: Props) {
  const { postQuestionURL, onAnswered } = props;
  const { api } = useAPI();
  const [error, setError] = useState<null | string>(null);
  const [answer, setAnswer] = useState<string>("");
  const { onRefresh } = useShipmentPageContext();

  // The callback is chained after the catch rather than inside the success
  // handler: a caller whose callback throws must not make a sent answer read as
  // unsent, and must not stop the page refreshing.
  const postAnswer = () => {
    api<unknown>("post", postQuestionURL, { answer })
      .then(() => {
        setError(null);
        if (onRefresh) {
          onRefresh();
        }
        return true;
      })
      .catch((e) => {
        setError(e?.response?.data?.message ?? "Failed to submit answer");
        return false;
      })
      .then((answered) => {
        if (answered) {
          onAnswered();
        }
      });
  };

  return (
    <div>
      <Stack direction="column" sx={{ mt: "1rem" }}>
        <Stack
          direction="row"
          justifyContent="center"
          spacing={2}
          alignItems="center"
        >
          <TextField
            fullWidth
            required
            label="Answer"
            onChange={(e) => setAnswer(e.target.value)}
            value={answer}
            multiline
            maxRows={6}
          />
          <Button
            disabled={answer.length < 1}
            variant="contained"
            sx={{ pl: "2rem", pr: "2rem" }}
            onClick={postAnswer}
          >
            Send
          </Button>
        </Stack>

        <ErrorTypography error={error} />
      </Stack>
    </div>
  );
}
